"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { CircleDiagram } from "@/components/tools/geometry/circle-diagram";
import {
  DiagramDownload,
  useDiagramRef,
} from "@/components/tools/geometry/diagram";
import {
  DECIMAL_PLACES,
  type DecimalPlaces,
  type Format,
  formatMeasure,
} from "@/components/tools/geometry/format";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type AngleInputUnit,
  parseAngleInput,
  parsePiNumber,
} from "@/lib/tool-kit/geometry/angle-input";
import {
  CIRCLE_GIVEN,
  type CircleBasics,
  type CircleGiven,
  computeCircle,
  computeSectorParts,
  type Measure,
  type SectorParts,
} from "@/lib/tool-kit/geometry/circle";
import { formatExact } from "@/lib/tool-kit/geometry/exact";
import { styleOfTexts } from "@/lib/tool-kit/geometry/measurements";
import {
  LENGTH_UNIT_IDS,
  type LengthUnitId,
  unitSymbol,
} from "@/lib/tool-kit/geometry/units";
import type { ResultFormat } from "@/lib/tool-kit/geometry/working";
import { decimalMarkOf } from "@/lib/tool-kit/math/locale";

/** English strings. A translated content file overrides these through `ui`. */
export const CIRCLE_CALCULATOR_STRINGS = {
  givenLabel: "I know the circle's",
  given: {
    radius: "Radius (r)",
    diameter: "Diameter (d)",
    circumference: "Circumference (C)",
    area: "Area (A)",
  },
  valueLabel: "Value",
  valueHint:
    "Whole numbers, decimals and fractions such as 3/4. Circumference and area may be written as a multiple of π, such as 10π, to keep the answer exact.",
  unitLabel: "Unit of length",
  decimalsLabel: "Decimal places",
  lengthUnits: {
    mm: "Millimeters (mm)",
    cm: "Centimeters (cm)",
    m: "Meters (m)",
    km: "Kilometers (km)",
    in: "Inches (in)",
    ft: "Feet (ft)",
    yd: "Yards (yd)",
    mi: "Miles (mi)",
  },
  results: "Results",
  measurementHeader: "Measurement",
  exactHeader: "Exact",
  decimalHeader: "Decimal",
  basics: {
    radius: "Radius (r)",
    diameter: "Diameter (d)",
    circumference: "Circumference (C)",
    area: "Area (A)",
  },
  workingHeading: "The working",
  partsHeading: "Arc, sector, chord and segment",
  partsIntro: "These use the radius found above.",
  angleLabel: "Central angle (θ)",
  angleUnitLabel: "Angle unit",
  angleUnits: { deg: "Degrees", rad: "Radians" },
  angleHint:
    "In radians, type a multiple of π such as pi/3 or 2π/3 to keep the answer exact.",
  parts: {
    arc: "Arc length (s)",
    sector: "Sector area",
    chord: "Chord length (c)",
    segment: "Segment area",
    sagitta: "Segment height (sagitta)",
    sectorPerimeter: "Sector perimeter",
    segmentPerimeter: "Segment perimeter",
  },
  partsWorking: "Working for the angle",
  figureHeading: "Figure",
  figureTitle:
    "A circle of radius {r} with a central angle of {angle}, its sector, chord and segment",
  downloadFigure: "Download figure (PNG)",
  copyResult: "Copy results",
  copied: "Copied!",
  enter: "Enter a value to see the circle.",
  invalid:
    "Enter the value as a number or fraction; circumference and area may also be a multiple of π, such as 10π.",
  positive: "The value must be greater than zero.",
  plain:
    "Enter the radius or diameter as a plain number — a multiple of π is only meaningful for circumference or area.",
  angleInvalid:
    "Enter the angle as a number; in radians you can also write multiples of π, such as pi/3.",
  angleRange: "Enter an angle greater than 0° and at most 360° (2π radians).",
  needRadius:
    "Enter a valid circle measurement above to find the arc and sector.",
} as const;

type Strings = typeof CIRCLE_CALCULATOR_STRINGS;

const ID_PREFIX = "circ";

interface RowSpec {
  label: string;
  measure: Measure;
  kind: "length" | "area";
}

function MeasureTable({
  rows,
  unit,
  strings,
  format,
  fmt,
  places,
}: {
  rows: RowSpec[];
  unit: LengthUnitId;
  strings: Strings;
  format: ResultFormat;
  fmt: Format;
  places: number;
}) {
  return (
    <div className={`${CARD} overflow-x-auto`}>
      <table className={TABLE}>
        <thead>
          <tr>
            <th className={TH} scope="col">
              {strings.measurementHeader}
            </th>
            <th className={TH} scope="col">
              {strings.exactHeader}
            </th>
            <th className={TH} scope="col">
              {strings.decimalHeader}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const suffix = ` ${unitSymbol(unit, row.kind === "area" ? 2 : 1)}`;
            return (
              <tr key={row.label}>
                <th className={`${TD} font-normal`} scope="row">
                  {row.label}
                </th>
                <td className="border-border border-b px-3 py-2 font-mono text-foreground">
                  {row.measure.exact
                    ? `${formatExact(row.measure.exact, {
                        decimals: format.style === "decimal",
                        mark: format.mark,
                      })}${suffix}`
                    : "—"}
                </td>
                <td className="border-border border-b px-3 py-2 font-mono text-foreground">
                  {formatMeasure(fmt, row.measure.value, places)}
                  {suffix}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function WorkingBox({ lines }: { lines: string[] }) {
  return (
    <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
      {lines.map((line) => (
        <p key={line}>{line}</p>
      ))}
    </div>
  );
}

function PartsSection({
  basics,
  angleText,
  angleUnit,
  unit,
  strings,
  format,
  places,
}: {
  basics: CircleBasics;
  angleText: string;
  angleUnit: AngleInputUnit;
  unit: LengthUnitId;
  strings: Strings;
  format: ResultFormat;
  places: number;
}) {
  const fmt = useToolNumber();
  const svgRef = useDiagramRef();
  const angle = parseAngleInput(angleText, angleUnit);
  if (angle === null) {
    return <ErrorNotice>{strings.angleInvalid}</ErrorNotice>;
  }
  const parts: SectorParts | { ok: false } = computeSectorParts(
    basics.radius,
    angle,
    format
  );
  if (!parts.ok) {
    return <ErrorNotice>{strings.angleRange}</ErrorNotice>;
  }
  const length = (value: number) =>
    `${formatMeasure(fmt, value, places)} ${unitSymbol(unit, 1)}`;
  const rows: RowSpec[] = [
    { label: strings.parts.arc, measure: parts.arc, kind: "length" },
    { label: strings.parts.sector, measure: parts.sector, kind: "area" },
    { label: strings.parts.chord, measure: parts.chord, kind: "length" },
    { label: strings.parts.segment, measure: parts.segment, kind: "area" },
    { label: strings.parts.sagitta, measure: parts.sagitta, kind: "length" },
    {
      label: strings.parts.sectorPerimeter,
      measure: parts.sectorPerimeter,
      kind: "length",
    },
    {
      label: strings.parts.segmentPerimeter,
      measure: parts.segmentPerimeter,
      kind: "length",
    },
  ];
  const angleLabel =
    angleUnit === "deg"
      ? `θ = ${angleText.trim()}°`
      : `θ = ${angle.radianText}`;
  return (
    <>
      <MeasureTable
        fmt={fmt}
        format={format}
        places={places}
        rows={rows}
        strings={strings}
        unit={unit}
      />
      <h3 className={WIDGET_H3}>{strings.partsWorking}</h3>
      <WorkingBox lines={parts.steps} />
      <h3 className={WIDGET_H3}>{strings.figureHeading}</h3>
      <div className="space-y-2">
        <CircleDiagram
          angleDegrees={parts.angleDegrees}
          labels={{
            radius: `r = ${length(basics.radius.value)}`,
            angle: angleLabel,
            arc: `s = ${length(parts.arc.value)}`,
            chord: `c = ${length(parts.chord.value)}`,
          }}
          svgRef={svgRef}
          title={t(strings.figureTitle, {
            r: length(basics.radius.value),
            angle: angleLabel.replace("θ = ", ""),
          })}
        />
        <DiagramDownload
          fileName="circle-sector-chord-segment"
          label={strings.downloadFigure}
          svgRef={svgRef}
        />
      </div>
    </>
  );
}

function BasicsSection({
  basics,
  unit,
  strings,
  format,
  places,
}: {
  basics: CircleBasics;
  unit: LengthUnitId;
  strings: Strings;
  format: ResultFormat;
  places: number;
}) {
  const fmt = useToolNumber();
  const rows: RowSpec[] = [
    { label: strings.basics.radius, measure: basics.radius, kind: "length" },
    {
      label: strings.basics.diameter,
      measure: basics.diameter,
      kind: "length",
    },
    {
      label: strings.basics.circumference,
      measure: basics.circumference,
      kind: "length",
    },
    { label: strings.basics.area, measure: basics.area, kind: "area" },
  ];
  const summary = rows
    .map((row) => {
      const suffix = ` ${unitSymbol(unit, row.kind === "area" ? 2 : 1)}`;
      return `${row.label}: ${formatMeasure(fmt, row.measure.value, places)}${suffix}`;
    })
    .join("\n");
  return (
    <>
      <MeasureTable
        fmt={fmt}
        format={format}
        places={places}
        rows={rows}
        strings={strings}
        unit={unit}
      />
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={summary}
      />
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      <WorkingBox lines={basics.steps} />
    </>
  );
}

function errorText(strings: Strings, error: "positive" | "plain"): string {
  return error === "positive" ? strings.positive : strings.plain;
}

export default function CircleCalculatorWidget() {
  const strings = useToolUi(CIRCLE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const mark = decimalMarkOf(fmt);
  const [given, setGiven] = useState<CircleGiven>("radius");
  const [valueText, setValueText] = useState("5");
  const [unit, setUnit] = useState<LengthUnitId>("cm");
  const [places, setPlaces] = useState<DecimalPlaces>("2");
  const [angleText, setAngleText] = useState("60");
  const [angleUnit, setAngleUnit] = useState<AngleInputUnit>("deg");

  const format: ResultFormat = { mark, style: styleOfTexts([valueText]) };
  const input = parsePiNumber(valueText);
  const result = input ? computeCircle(given, input, format) : null;
  let body: React.ReactNode;
  if (valueText.trim() === "") {
    body = <Notice>{strings.enter}</Notice>;
  } else if (result === null) {
    body = <ErrorNotice>{strings.invalid}</ErrorNotice>;
  } else if (result.ok) {
    body = (
      <BasicsSection
        basics={result}
        format={format}
        places={Number(places)}
        strings={strings}
        unit={unit}
      />
    );
  } else {
    body = <ErrorNotice>{errorText(strings, result.error)}</ErrorNotice>;
  }
  const basics = result?.ok ? result : null;

  return (
    <>
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id={`${ID_PREFIX}-given`}
            label={strings.givenLabel}
            onChange={setGiven}
            options={CIRCLE_GIVEN.map((id) => ({
              value: id,
              label: strings.given[id],
            }))}
            value={given}
          />
          <TextField
            hint={strings.valueHint}
            id={`${ID_PREFIX}-value`}
            label={strings.valueLabel}
            mono
            onChange={setValueText}
            value={valueText}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id={`${ID_PREFIX}-unit`}
            label={strings.unitLabel}
            onChange={setUnit}
            options={LENGTH_UNIT_IDS.map((id) => ({
              value: id,
              label: strings.lengthUnits[id],
            }))}
            value={unit}
          />
          <SelectField
            id={`${ID_PREFIX}-places`}
            label={strings.decimalsLabel}
            onChange={setPlaces}
            options={DECIMAL_PLACES.map((value) => ({ value, label: value }))}
            value={places}
          />
        </div>
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body}
      </section>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.partsHeading}</h2>
        <p className="text-muted-foreground text-sm">{strings.partsIntro}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            hint={angleUnit === "rad" ? strings.angleHint : undefined}
            id={`${ID_PREFIX}-angle`}
            label={strings.angleLabel}
            mono
            onChange={setAngleText}
            value={angleText}
          />
          <SelectField
            id={`${ID_PREFIX}-angle-unit`}
            label={strings.angleUnitLabel}
            onChange={(next) => {
              setAngleUnit(next);
              setAngleText(next === "deg" ? "60" : "pi/3");
            }}
            options={(["deg", "rad"] as const).map((id) => ({
              value: id,
              label: strings.angleUnits[id],
            }))}
            value={angleUnit}
          />
        </div>
        {basics ? (
          <PartsSection
            angleText={angleText}
            angleUnit={angleUnit}
            basics={basics}
            format={format}
            places={Number(places)}
            strings={strings}
            unit={unit}
          />
        ) : (
          <Notice>{strings.needRadius}</Notice>
        )}
      </section>
    </>
  );
}
